import { Box, Paper, Typography } from "@mui/material";
import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import AllProductContext from "../../../../context/allProducts/AllProductsContext";
import AllCategoriesContext from "../../../../context/allCategories/AllCategoriesContext";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CategoryPlaceholder from "../../../../assets/images/placeholder/category.jpg";
import { categoryHasDirectProducts } from "../../../../utils/categoryProducts";

const MAX_PRODUCTS = 8;

const highlightMatch = (text, query) => {
  if (!text) return text;
  const q = query.trim();
  if (!q) return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const start = lower.indexOf(needle);
  if (start === -1) return text;
  const end = start + q.length;
  return (
    <>
      {text.slice(0, start)}
      <mark className="search-hit">{text.slice(start, end)}</mark>
      {text.slice(end)}
    </>
  );
};

const productLink = (product) => {
  const categoryId = product?.parentCategory?.id || "all";
  return `/collections/${categoryId}/${product.id}`;
};

const SearchResultsBox = ({ styles, searchText, cancelSearchHandle }) => {
  const allProductsContext = useContext(AllProductContext);
  const allCategoriesContext = useContext(AllCategoriesContext);
  const query = (searchText || "").trim();

  const { products, categories, extraProductCount } = useMemo(() => {
    const needle = query.toLowerCase();
    const allProducts = allProductsContext.state || [];
    const allCategories = allCategoriesContext.state || [];

    const matchedProducts = allProducts
      .filter((product) => product.title?.toLowerCase().includes(needle))
      .sort((a, b) => {
        const aStarts = a.title.toLowerCase().startsWith(needle) ? 0 : 1;
        const bStarts = b.title.toLowerCase().startsWith(needle) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return (a.listPrice || 0) - (b.listPrice || 0);
      });

    const matchedCategories = allCategories
      .filter(
        (category) =>
          category.name?.toLowerCase().includes(needle) &&
          categoryHasDirectProducts(category.id, allProducts)
      )
      .sort((a, b) => {
        const aStarts = a.name.toLowerCase().startsWith(needle) ? 0 : 1;
        const bStarts = b.name.toLowerCase().startsWith(needle) ? 0 : 1;
        return aStarts - bStarts;
      });

    return {
      products: matchedProducts.slice(0, MAX_PRODUCTS),
      categories: matchedCategories,
      extraProductCount: Math.max(0, matchedProducts.length - MAX_PRODUCTS),
      totalProducts: matchedProducts.length,
    };
  }, [allProductsContext.state, allCategoriesContext.state, query]);

  const hasResults = products.length > 0 || categories.length > 0;

  return (
    <Paper className="search-dropdown" sx={styles} elevation={0}>
      <Box className="search-dropdown-head">
        <Typography className="search-dropdown-query" component="p">
          {hasResults ? "Results for" : "No results for"}{" "}
          <strong>“{query}”</strong>
        </Typography>
        {hasResults && (
          <Typography className="search-dropdown-meta" component="p">
            {products.length + extraProductCount > 0 &&
              `${products.length + extraProductCount} product${
                products.length + extraProductCount === 1 ? "" : "s"
              }`}
            {products.length + extraProductCount > 0 &&
              categories.length > 0 &&
              " · "}
            {categories.length > 0 &&
              `${categories.length} categor${
                categories.length === 1 ? "y" : "ies"
              }`}
          </Typography>
        )}
      </Box>

      <Box className="search-dropdown-body">
        {!hasResults && (
          <Box className="search-empty">
            <SearchOffOutlinedIcon className="search-empty-icon" />
            <Typography className="search-empty-title">
              Nothing matched that search
            </Typography>
            <Typography className="search-empty-hint">
              Try a product name, oil, or category like Shilajeet
            </Typography>
          </Box>
        )}

        {products.length > 0 && (
          <Box className="search-section">
            <Typography className="search-section-label">Products</Typography>
            <Box className="search-product-list">
              {products.map((product) => {
                const img = product.images?.[0]?.url;
                const hasDiscount =
                  product.maxPrice > product.listPrice && product.listPrice > 0;
                const discount = hasDiscount
                  ? Math.ceil(
                      ((product.maxPrice - product.listPrice) /
                        product.maxPrice) *
                        100
                    )
                  : 0;
                return (
                  <Link
                    key={product.id}
                    to={productLink(product)}
                    className="search-product-row"
                    onClick={cancelSearchHandle}
                  >
                    <span
                      className="search-product-thumb"
                      style={
                        img ? { backgroundImage: `url(${img})` } : undefined
                      }
                    />
                    <span className="search-product-copy">
                      <span className="search-product-title">
                        {highlightMatch(product.title, query)}
                      </span>
                      <span className="search-product-price">
                        <strong>₹{product.listPrice}</strong>
                        {hasDiscount && <s>₹{product.maxPrice}</s>}
                        {discount > 0 && <em>{discount}% off</em>}
                      </span>
                    </span>
                    <ChevronRightIcon className="search-row-chevron" />
                  </Link>
                );
              })}
            </Box>
            {extraProductCount > 0 && (
              <Typography className="search-more">
                +{extraProductCount} more product
                {extraProductCount === 1 ? "" : "s"} — refine your search
              </Typography>
            )}
          </Box>
        )}

        {categories.length > 0 && (
          <Box className="search-section search-section-categories">
            <Typography className="search-section-label">
              Shop by category
            </Typography>
            <Box className="search-category-list">
              {categories.map((category) => {
                const img = category.images?.[0]?.url || CategoryPlaceholder;
                return (
                  <Link
                    key={category.id}
                    to={`/collections/${category.id}`}
                    className="search-category-chip"
                    onClick={cancelSearchHandle}
                  >
                    <span
                      className="search-category-thumb"
                      style={{ backgroundImage: `url(${img})` }}
                    />
                    <span className="search-category-name">
                      {highlightMatch(category.name, query)}
                    </span>
                  </Link>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default SearchResultsBox;
